import { DatePipe } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  CalendarEvent,
  CalendarMonthViewDay,
  CalendarView,
} from "angular-calendar";
import { format, lastDayOfMonth, lastDayOfWeek, startOfWeek } from "date-fns";
import { asyncScheduler } from "rxjs";
import { AppState } from "../../../../core/app-state";
import { FesBaseComponent } from "../../fes-base.component";
import { TableColumn, TableViewFilterMode } from "../table-view-component-base";

export const parseLocalDate = (strDate: string) => {
  //TODO - name this func better
  const dateArgs = strDate && /(\d{4})-(\d{2})(-|$)(\d{2})?/.exec(strDate);
  if (dateArgs) {
    const [_, strYear, strMonth, __, strDay] = dateArgs;
    return strDay
      ? new Date(parseInt(strYear), parseInt(strMonth) - 1, parseInt(strDay))
      : new Date(parseInt(strYear), parseInt(strMonth) - 1, 1);
  }
  return null;
};

@Component({
  selector: "app-table-view-calendar",
  templateUrl: "./table-view-calendar.component.html",
  styleUrls: ["./table-view-calendar.component.scss"],
})
export class TableViewCalendarComponent
  extends FesBaseComponent
  implements OnInit, OnDestroy
{
  @Input() defaultDateFilterResolverFn: (dateColumn: TableColumn) => string = (dateColumn) => "";

  @Input("column") set _setColumn$(column: TableColumn) {
    this.column = column;
    this.setDateFilter(column.filter.value);
  }

  @Input("viewMode") set _setViewMode(strMode: "day" | "week" | "month") {
    let mode: CalendarView = CalendarView.Month;
    switch (strMode) {
      case "day":
        mode = CalendarView.Day;
        break;
      case "week":
        mode = CalendarView.Week;
        break;
    }
    this.setViewMode(mode);
  }

  // Set calendar date from the task list filter text field value
  @Input("dateFilter") set _dateFilter(strDate: string) {
    this.setDateFilter(strDate);
  }

  setDateFilter(strDate: string) {
    const parsedDate = parseLocalDate(strDate);
    this.viewDateFormVal = strDate;
    if (parsedDate) {
      this.viewDate = parsedDate;
    } else {
      asyncScheduler.schedule(() => {
        if (this.column && this.defaultDateFilterResolverFn) {
          const datePattern = this.defaultDateFilterResolverFn(this.column);
          const parsedDefaultDate = parseLocalDate(datePattern);
          if ( parsedDefaultDate ) {
            this.viewDate = parsedDefaultDate;
          }
        }
        this.viewDateChanged(this.viewDate);
      })
    }
  }

  @Input() viewActive = true; // free some resources if tab is not active in UI

  @Input("events") set _eventsSetter(events: CalendarEvent[]) {
    this.events = events || [];
  }
  events: CalendarEvent[];

  @Input("groupedByDay") set _groupedByDaySetter(
    gbd: { day: string; day_total: number }[]
  ) {
    this.groupedByDay = {};
    //TODO TypeORM getRawMany returned the day_total as a string (convert to int)
    gbd?.forEach((dc) => (this.groupedByDay[dc.day] = dc.day_total));
    //console.log(`groupedByDay: ${JSON.stringify(this.groupedByDay)}`);
  }
  groupedByDay: { [day: string]: number };

  @Output("dateFilterChanged") dateFilterChanged = new EventEmitter<{
    pattern: string;
    mode: TableViewFilterMode;
  }>();

  @Output() closeCalendar = new EventEmitter<{clearFilter: boolean}>();

  viewMode: CalendarView = CalendarView.Month;
  viewDate: Date = new Date();
  CalendarView = CalendarView;
  column: TableColumn;
  viewDateFormVal: string;
  bEditDateMode = false;


  constructor(public appState: AppState, private datePipe: DatePipe) {
    super(appState);
  }

  ngOnInit(): void {
    this.setViewMode(this.viewMode);
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  setViewMode(viewMode: CalendarView) {
    this.viewMode = viewMode;
    this.viewDateChanged(this.viewDate);
  }

  beginEditDateMode() {
    this.bEditDateMode = true;
    this.viewDateFormVal = this.dateToString(this.viewDate, CalendarView.Day);
  }

  acceptFormDate() {
    const date = parseLocalDate(this.viewDateFormVal);
    console.log(`acceptFormDate() input.value="${this.viewDateFormVal}", parsed date: ${date}`)
    this.viewDateChanged(date);
    this.bEditDateMode = false;
  }

  rejectFormDate() {
    this.viewDateFormVal = null;
    this.bEditDateMode = false;
  }

  viewDateChanged(viewDate: Date) {
    this.viewDate = viewDate;
    let strMode: TableViewFilterMode =
      this.viewMode == CalendarView.Week ? "~*" : "contains";
    let strDateFilter = this.dateToString(viewDate, this.viewMode);
    this.viewDateFormVal = strDateFilter;
    this.dateFilterChanged.emit({ pattern: strDateFilter, mode: strMode });
  }

  dateToString(date: Date, mode: CalendarView) {
    let strDateFilter = "";
    switch (mode) {
      case CalendarView.Day:
        strDateFilter = `${format(date, "yyyy-MM-dd")}`;
        break;
      case CalendarView.Week:
        const startOfWeekDate = startOfWeek(date);
        const lastDayOfMonthDate = lastDayOfMonth(startOfWeekDate);
        strDateFilter = `${format(startOfWeekDate, "yyyy-MM-")}`;
        const ifirstDayOfWeek = startOfWeekDate.getDate();
        const iLastDayOfMonth = lastDayOfMonthDate.getDate();
        //console.log(`startOfWeek=${startOfWeekDate}, firstDayOfWeek=${ifirstDayOfWeek}`)
        const days = [0, 1, 2, 3, 4, 5, 6]
          .map((i) => i + ifirstDayOfWeek)
          .filter((d) => d <= iLastDayOfMonth);
        const daysRegEx = days.map((d) => `${d}`.padStart(2, "0")).join("|");
        strDateFilter += `(${daysRegEx})`;
        if (days.length < 7) {
          const lastDayOfWeekDate = lastDayOfWeek(date);
          const iLastDayOfWeek = lastDayOfWeekDate.getDate();
          strDateFilter += `|${format(lastDayOfWeekDate, "yyyy-MM-")}`;
          const days = Array.from(Array(iLastDayOfWeek).keys()).map(
            (i) => i + 1
          );
          const daysRegEx = days.map((d) => `${d}`.padStart(2, "0")).join("|");
          strDateFilter += `(${daysRegEx})`;
          strDateFilter = `^(${strDateFilter})`;
        }
        break;
      default:
        strDateFilter = `${format(date, "yyyy-MM")}`;
        break;
    }
    return strDateFilter;
  }

  onCalendarEventSelected(event: CalendarEvent) {
    console.log(`onCalendarEventSelected(${event?.title})`);
  }

  onCalendarDaySelected(day: CalendarMonthViewDay) {
    this.viewDate = day.date;
    this.setViewMode(CalendarView.Day);
  }
}
